import axios from 'axios';

export const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

const api = axios.create({ baseURL: API_URL });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('ck_admin_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (error) => {
    if (error.response?.status === 401 && !window.location.pathname.includes('/login')) {
      localStorage.removeItem('ck_admin_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const errText = (e) =>
  e?.response?.data?.message
    ? Array.isArray(e.response.data.message)
      ? e.response.data.message.join(', ')
      : e.response.data.message
    : e?.message || 'Произошла ошибка';

export default api;

// ============================================================
// API
// ============================================================

const get = (url, params) => api.get(url, { params }).then((r) => r.data);
const post = (url, data) => api.post(url, data).then((r) => r.data);
const patch = (url, data) => api.patch(url, data).then((r) => r.data);
const del = (url) => api.delete(url).then((r) => r.data);

export const authApi = {
  login: (email, password) => post('/auth/admin/login', { email, password }),
  me: () => get('/auth/admin/me'),
  changePassword: (oldPassword, newPassword) =>
    post('/auth/admin/change-password', { oldPassword, newPassword }),
};

export const analyticsApi = {
  dashboard: () => get('/admin/analytics/dashboard'),
  insights: () => get('/admin/analytics/insights'),
  revenue: (days = 30) => get('/admin/analytics/revenue', { days }),
  weekday: (weeks = 8) => get('/admin/analytics/weekday', { weeks }),
  hourly: (days = 30) => get('/admin/analytics/hourly', { days }),
  forecast: (date, weeks = 8) => get('/admin/analytics/forecast', { date, weeks }),
  forecastIngredients: (date) => get('/admin/analytics/forecast/ingredients', { date }),
  dishes: (days = 30) => get('/admin/analytics/dishes', { days }),
  customers: (days = 30) => get('/admin/analytics/customers', { days }),
  delivery: (days = 30) => get('/admin/analytics/delivery', { days }),
  slots: (days = 14) => get('/admin/analytics/slots', { days }),
  promotions: (days = 30) => get('/admin/analytics/promotions', { days }),
};

export const catalogApi = {
  categories: () => get('/admin/categories'),
  createCategory: (data) => post('/admin/categories', data),
  updateCategory: (id, data) => patch(`/admin/categories/${id}`, data),
  deleteCategory: (id) => del(`/admin/categories/${id}`),

  dishes: (params) => get('/admin/dishes', params),
  dish: (id) => get(`/admin/dishes/${id}`),
  createDish: (data) => post('/admin/dishes', data),
  updateDish: (id, data) => patch(`/admin/dishes/${id}`, data),
  deleteDish: (id) => del(`/admin/dishes/${id}`),
  setAvailability: (id, isAvailable) => patch(`/admin/dishes/${id}/availability`, { isAvailable }),
  setRecipe: (id, items) => post(`/admin/dishes/${id}/recipe`, { items }),
  recalcCosts: () => post('/admin/dishes/recalc-costs'),

  promotions: () => get('/admin/promotions'),
  createPromotion: (data) => post('/admin/promotions', data),
  updatePromotion: (id, data) => patch(`/admin/promotions/${id}`, data),
  deletePromotion: (id) => del(`/admin/promotions/${id}`),

  reviews: (dishId) => get('/admin/reviews', { dishId }),
};

export const ordersApi = {
  list: (params) => get('/admin/orders', params),
  board: () => get('/admin/orders/board'),
  one: (id) => get(`/admin/orders/${id}`),
  updateStatus: (id, status, cancelReason) =>
    patch(`/admin/orders/${id}/status`, { status, cancelReason }),
};

export const slotsApi = {
  list: () => get('/admin/time-slots'),
  load: (date) => get('/admin/time-slots/load', { date }),
  create: (data) => post('/admin/time-slots', data),
  update: (id, data) => patch(`/admin/time-slots/${id}`, data),
  remove: (id) => del(`/admin/time-slots/${id}`),
};

export const packagesApi = {
  list: () => get('/admin/packages'),
  one: (id) => get(`/admin/packages/${id}`),
  create: (data) => post('/admin/packages', data),
  update: (id, data) => patch(`/admin/packages/${id}`, data),
  remove: (id) => del(`/admin/packages/${id}`),
  subscriptions: (status) => get('/admin/packages/subscriptions', { status }),
  updateSubStatus: (id, status) => patch(`/admin/packages/subscriptions/${id}/status`, { status }),
  guaranteedDemand: (date) => get('/admin/packages/guaranteed-demand', { date }),
};

export const inventoryApi = {
  list: (params) => get('/admin/inventory', params),
  one: (id) => get(`/admin/inventory/${id}`),
  create: (data) => post('/admin/inventory', data),
  update: (id, data) => patch(`/admin/inventory/${id}`, data),
  remove: (id) => del(`/admin/inventory/${id}`),
  summary: () => get('/admin/inventory/summary'),
  runway: () => get('/admin/inventory/runway'),
  purchaseList: (days = 7) => get('/admin/inventory/purchase-list', { days }),
  movements: (params) => get('/admin/inventory/movements', params),
  addMovement: (data) => post('/admin/inventory/movements', data),
  suppliers: () => get('/admin/inventory/suppliers'),
  createSupplier: (data) => post('/admin/inventory/suppliers', data),
  updateSupplier: (id, data) => patch(`/admin/inventory/suppliers/${id}`, data),
};

export const corporateApi = {
  organizations: () => get('/admin/corporate/organizations'),
  organization: (id) => get(`/admin/corporate/organizations/${id}`),
  createOrganization: (data) => post('/admin/corporate/organizations', data),
  updateOrganization: (id, data) => patch(`/admin/corporate/organizations/${id}`, data),
  topUp: (id, amount, note) => post(`/admin/corporate/organizations/${id}/top-up`, { amount, note }),
  report: (id, days = 30) => get(`/admin/corporate/organizations/${id}/report`, { days }),
  departments: (id) => get(`/admin/corporate/organizations/${id}/departments`),
  createDepartment: (id, data) => post(`/admin/corporate/organizations/${id}/departments`, data),
  updateDepartment: (id, data) => patch(`/admin/corporate/departments/${id}`, data),
  deleteDepartment: (id) => del(`/admin/corporate/departments/${id}`),
  admins: () => get('/admin/corporate/admins'),
};

export const employeesApi = {
  list: (params) => get('/admin/employees', params),
  one: (id) => get(`/admin/employees/${id}`),
  create: (data) => post('/admin/employees', data),
  update: (id, data) => patch(`/admin/employees/${id}`, data),
  remove: (id) => del(`/admin/employees/${id}`),
  grantCashback: (id, amount, note) => post(`/admin/employees/${id}/cashback`, { amount, note }),
  bulk: (organizationId, rows) => post('/admin/employees/bulk', { organizationId, rows }),
};

export const broadcastApi = {
  list: () => get('/admin/broadcasts'),
  one: (id) => get(`/admin/broadcasts/${id}`),
  preview: (audience, departmentId) => get('/admin/broadcasts/preview', { audience, departmentId }),
  create: (data) => post('/admin/broadcasts', data),
  update: (id, data) => patch(`/admin/broadcasts/${id}`, data),
  send: (id) => post(`/admin/broadcasts/${id}/send`),
  remove: (id) => del(`/admin/broadcasts/${id}`),
};

export const settingsApi = {
  get: () => get('/admin/settings'),
  update: (data) => patch('/admin/settings', data),
};
